import { type Action, combineReducers, configureStore, type Middleware, type Reducer, type ThunkDispatch } from "@reduxjs/toolkit";
import { type TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { persistReducer, persistStore } from "redux-persist";
import { authApi } from "@/src/auth/services/auth-http.service";
import { authPersistConfig, authSlice, logOut } from "@/src/auth/store/auth-slice";
import { env } from "@/src/common/constants/env";
import { registerCsrfTokenProvider } from "@/src/common/services/http.service";
import { paymentApi } from "@/src/common/services/payment-http.service";
import { bookingApi } from "@/src/owner/services/booking-http.service";
import { cautionApi } from "@/src/owner/services/caution-http.service";
import { contractApi } from "@/src/owner/services/contract-http.service";
import { contractTemplateApi } from "@/src/owner/services/contract-template-http.service";
import { icalSyncApi } from "@/src/owner/services/ical-sync-http.service";
import { integrationApi } from "@/src/owner/services/integration-http.service";
import { ownerDocumentApi } from "@/src/owner/services/owner-document-http.service";
import { ownerProfileApi } from "@/src/owner/services/owner-profile-http.service";
import { propertyDayPriceApi } from "@/src/owner/services/property-day-price-http.service";
import { propertyApi } from "@/src/owner/services/property-http.service";
import { propertyLengthDiscountApi } from "@/src/owner/services/property-length-discount-http.service";
import { teamApi } from "@/src/owner/services/team-http.service";
import { tenantApi } from "@/src/owner/services/tenant-http.service";
import { uploadApi } from "@/src/owner/services/upload-http.service";
import { publicApi } from "@/src/public/services/public-http.service";
import { tenantBookingApi } from "@/src/tenant/services/tenant-booking-http.service";
import { tenantCautionApi } from "@/src/tenant/services/tenant-caution-http.service";
import { tenantPaymentApi } from "@/src/tenant/services/tenant-payment-http.service";
import { tenantProfileApi } from "@/src/tenant/services/tenant-profile-http.service";
import { generalPersistConfig, generalSlice } from "../store/general-slice";

const combinedReducer = combineReducers({
	[authSlice.name]: persistReducer(authPersistConfig, authSlice.reducer),
	[authApi.reducerPath]: authApi.reducer,
	[propertyApi.reducerPath]: propertyApi.reducer,
	[propertyDayPriceApi.reducerPath]: propertyDayPriceApi.reducer,
	[propertyLengthDiscountApi.reducerPath]: propertyLengthDiscountApi.reducer,
	[bookingApi.reducerPath]: bookingApi.reducer,
	[cautionApi.reducerPath]: cautionApi.reducer,
	[contractTemplateApi.reducerPath]: contractTemplateApi.reducer,
	[contractApi.reducerPath]: contractApi.reducer,
	[ownerProfileApi.reducerPath]: ownerProfileApi.reducer,
	[ownerDocumentApi.reducerPath]: ownerDocumentApi.reducer,
	[integrationApi.reducerPath]: integrationApi.reducer,
	[icalSyncApi.reducerPath]: icalSyncApi.reducer,
	[teamApi.reducerPath]: teamApi.reducer,
	[tenantApi.reducerPath]: tenantApi.reducer,
	[uploadApi.reducerPath]: uploadApi.reducer,
	[tenantBookingApi.reducerPath]: tenantBookingApi.reducer,
	[tenantCautionApi.reducerPath]: tenantCautionApi.reducer,
	[tenantPaymentApi.reducerPath]: tenantPaymentApi.reducer,
	[tenantProfileApi.reducerPath]: tenantProfileApi.reducer,
	[paymentApi.reducerPath]: paymentApi.reducer,
	[publicApi.reducerPath]: publicApi.reducer,
	[generalSlice.name]: persistReducer(generalPersistConfig, generalSlice.reducer),
});

type CombinedState = ReturnType<typeof combinedReducer>;

const rootReducer: Reducer<CombinedState> = (state, action) => {
	if (action.type === logOut.type) {
		const general = state?.[generalSlice.name];
		return combinedReducer({ [generalSlice.name]: general } as CombinedState, action);
	}
	return combinedReducer(state, action);
};

export const store = configureStore({
	reducer: rootReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: false,
		}).concat(
			authApi.middleware,
			propertyApi.middleware,
			propertyDayPriceApi.middleware,
			propertyLengthDiscountApi.middleware,
			bookingApi.middleware,
			cautionApi.middleware,
			contractTemplateApi.middleware,
			contractApi.middleware,
			ownerProfileApi.middleware,
			ownerDocumentApi.middleware,
			integrationApi.middleware,
			icalSyncApi.middleware,
			teamApi.middleware,
			tenantApi.middleware,
			uploadApi.middleware,
			tenantBookingApi.middleware,
			tenantCautionApi.middleware,
			tenantPaymentApi.middleware,
			tenantProfileApi.middleware,
			paymentApi.middleware,
			publicApi.middleware,
		),
	devTools: env.MODE !== "production",
});

export const persistor = persistStore(store);

registerCsrfTokenProvider(() => store.getState().auth.csrfToken);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export type AppMiddlewareDispatch = ThunkDispatch<RootState, undefined, Action>;
export type AppMiddleware = Middleware<object, RootState, AppMiddlewareDispatch>;
